import React, { useState, useMemo, useRef, useEffect } from "react";
import StockRow from "./StockRow";
import { CaretUp, CaretDown, ArrowsDownUp, CaretDown as Chev } from "@phosphor-icons/react";
import useLivePrices, { bareSymbol } from "../../hooks/useLivePrices";

/**
 * Universal list container — renders a borderless card with a header
 * (title · subtitle · count) and a flat list of `StockRow`s inside.
 * Used by every scanner tab for a consistent feel.
 *
 * Built-in client-side sort:
 *   - Period selector (TODAY / 1W / 1M) — choose which % change to use
 *   - Direction pill — cycles Default → DESC (% High→Low) → ASC (% Low→High)
 * 1W and 1M values are derived from the row's `sparkline` array
 * (last 30 closes). Rows without sparkline fall back to today only.
 */
const SORT_STATES = ["default", "desc", "asc"];
const PERIOD_OPTIONS = [
  { k: "today", label: "Today", days: 1 },
  { k: "1w", label: "1 Week", days: 5 },
  { k: "1m", label: "1 Month", days: 22 },
];

function pctOverPeriod(item, periodKey) {
  if (periodKey === "today") {
    return typeof item?.pct_change_today === "number"
      ? item.pct_change_today
      : null;
  }
  const sp = item?.sparkline;
  if (!Array.isArray(sp) || sp.length < 2) return null;
  const lookback = periodKey === "1w" ? 5 : 22;
  const ref = sp.length > lookback ? sp[sp.length - 1 - lookback] : sp[0];
  const last = sp[sp.length - 1];
  if (!ref || !last) return null;
  return ((last - ref) / ref) * 100;
}

export default function StockList({
  title,
  subtitle,
  items,
  loading,
  emptyMessage = "No results match current filters.",
  onOpen,
  onWatch,
  watchedSet,
  freshSet,
  testIdPrefix = "row",
  rowProps = () => ({}),
  testid,
  rightHeader,
}) {
  const [sortDir, setSortDir] = useState("default");
  const [period, setPeriod] = useState("today");
  const [periodOpen, setPeriodOpen] = useState(false);
  const periodRef = useRef(null);

  // Close period dropdown on outside click
  useEffect(() => {
    if (!periodOpen) return;
    const onDocClick = (e) => {
      if (periodRef.current && !periodRef.current.contains(e.target)) {
        setPeriodOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [periodOpen]);

  // Compute the chosen-period pct for each item (memoised) and decorate.
  const decorated = useMemo(() => {
    if (!Array.isArray(items)) return items;
    return items.map((it) => ({
      ...it,
      _sort_pct: pctOverPeriod(it, period),
    }));
  }, [items, period]);

  // Subscribe to /api/live-prices (1-min poll) for every symbol in the
  // list. Symbols not present in the Upstox snapshot are silently skipped
  // and the row keeps showing its EOD price.
  const liveSymbols = useMemo(() => {
    if (!Array.isArray(items)) return [];
    return items
      .map((it) => bareSymbol(it.symbol))
      .filter(Boolean);
  }, [items]);
  const { prices: livePrices } = useLivePrices(liveSymbols, 60000);

  // Merge live prices into the decorated items so StockRow re-renders
  // with the latest LTP + today % change without any extra prop plumbing.
  const liveItems = useMemo(() => {
    if (!Array.isArray(decorated)) return decorated;
    if (!livePrices || Object.keys(livePrices).length === 0) return decorated;
    return decorated.map((it) => {
      const key = bareSymbol(it.symbol);
      const live = livePrices[key];
      if (!live || typeof live.last_price !== "number") return it;
      return {
        ...it,
        last_price: live.last_price,
        pct_change_today: typeof live.pct_change === "number"
          ? live.pct_change
          : it.pct_change_today,
        _live: true,
      };
    });
  }, [decorated, livePrices]);

  const sortedItems = useMemo(() => {
    if (sortDir === "default" || !Array.isArray(liveItems)) return liveItems;
    const arr = [...liveItems];
    arr.sort((a, b) => {
      const av = typeof a._sort_pct === "number" ? a._sort_pct : -1e9;
      const bv = typeof b._sort_pct === "number" ? b._sort_pct : -1e9;
      return sortDir === "desc" ? bv - av : av - bv;
    });
    return arr;
  }, [liveItems, sortDir]);

  const cycleSort = () => {
    const idx = SORT_STATES.indexOf(sortDir);
    setSortDir(SORT_STATES[(idx + 1) % SORT_STATES.length]);
  };
  const periodLabel = PERIOD_OPTIONS.find((p) => p.k === period)?.label || "Today";
  const dirLabel =
    sortDir === "desc"
      ? "High → Low"
      : sortDir === "asc"
      ? "Low → High"
      : "Default";
  const DirIcon =
    sortDir === "desc" ? CaretDown : sortDir === "asc" ? CaretUp : ArrowsDownUp;

  const showHeader = title || subtitle || rightHeader || (items && items.length > 0);

  return (
    <section data-testid={testid}>
      {showHeader && (
        <div className="flex items-baseline justify-between mb-3 border-b border-subtle pb-3 gap-3 flex-wrap">
          <div className="min-w-0">
            {title && (
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-fg">
                /// {title}
              </div>
            )}
            {subtitle && (
              <div className="text-xs text-muted-fg mt-1">{subtitle}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {rightHeader}
            {items && items.length > 1 && (
              <div className="flex items-center gap-1" data-testid={`${testIdPrefix}-sort-bar`}>
                {/* Period dropdown */}
                <div className="relative" ref={periodRef}>
                  <button
                    type="button"
                    onClick={() => setPeriodOpen((o) => !o)}
                    data-testid={`${testIdPrefix}-sort-period`}
                    aria-haspopup="listbox"
                    aria-expanded={periodOpen}
                    title="Choose timeframe for % change"
                    className="flex items-center gap-1 px-2.5 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover hover:border-pos/40 transition"
                  >
                    % {periodLabel}
                    <Chev size={10} weight="bold" className={"transition " + (periodOpen ? "rotate-180" : "")} />
                  </button>
                  {periodOpen && (
                    <div
                      role="listbox"
                      className="absolute right-0 top-full mt-1 z-30 min-w-[8rem] border border-subtle bg-surface rounded-sm shadow-xl overflow-hidden"
                      data-testid={`${testIdPrefix}-sort-period-menu`}
                    >
                      {PERIOD_OPTIONS.map((p) => (
                        <button
                          key={p.k}
                          type="button"
                          role="option"
                          aria-selected={period === p.k}
                          onClick={() => {
                            setPeriod(p.k);
                            setPeriodOpen(false);
                          }}
                          data-testid={`${testIdPrefix}-sort-period-${p.k}`}
                          className={
                            "block w-full text-left px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest transition " +
                            (period === p.k
                              ? "bg-pos-soft text-pos"
                              : "text-secondary-fg hover:bg-surface-hover hover:text-primary-fg")
                          }
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Direction pill */}
                <button
                  type="button"
                  onClick={cycleSort}
                  data-testid={`${testIdPrefix}-sort-toggle`}
                  title="Click to cycle: Default → High to Low → Low to High"
                  className={
                    "flex items-center gap-1.5 px-2.5 py-1 border rounded-sm text-[10px] font-mono uppercase tracking-widest transition " +
                    (sortDir !== "default"
                      ? "border-pos text-pos bg-pos-soft/40"
                      : "border-subtle text-secondary-fg hover:bg-surface-hover hover:border-pos/40")
                  }
                >
                  <DirIcon size={11} weight="bold" />
                  {dirLabel}
                </button>
              </div>
            )}
            <div className="font-mono text-lg tabular-nums text-primary-fg ml-1">
              {items?.length || 0}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-12 border border-subtle bg-surface rounded-sm animate-pulse"
            />
          ))}
        </div>
      ) : !sortedItems || sortedItems.length === 0 ? (
        <div className="p-6 border border-subtle rounded-sm bg-surface text-center text-sm text-muted-fg">
          {emptyMessage}
        </div>
      ) : (
        <div className="border border-subtle rounded-sm bg-surface/60 overflow-hidden">
          {sortedItems.map((s, idx) => {
            const extra = rowProps(s, idx) || {};
            // Append period % to the row's right hint when a non-default
            // period is selected, so users can see what they're sorting on.
            let hint = extra.rightHint;
            if (period !== "today" && typeof s._sort_pct === "number") {
              const pctTxt = `${s._sort_pct >= 0 ? "+" : ""}${s._sort_pct.toFixed(1)}% ${period.toUpperCase()}`;
              hint = hint ? `${pctTxt} · ${hint}` : pctTxt;
            }
            return (
              <StockRow
                key={`${s.symbol}-${s.signal_type || s.kind || s.direction || ""}-${s.first_detected_at || idx}`}
                item={s}
                onOpen={onOpen}
                onWatch={onWatch}
                watched={watchedSet ? watchedSet.has(s.symbol) : false}
                testIdPrefix={testIdPrefix}
                {...extra}
                rightHint={
                  freshSet && freshSet.has(`${s.symbol}:${s.signal_type}`)
                    ? "FRESH"
                    : hint
                }
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
